import { FileOutlined } from "@ant-design/icons";
import { Flex, Image, Typography } from "antd";
import { type CollectionFileFragment, VariantKind } from "../gql/generated/graphqlSchema.tsx";
import { formatFileSize } from "../utils/format.ts";
import { css } from "@linaria/core";

const wrapper = css`
  border-radius: var(--ant-border-radius);
  border: 1px solid var(--ant-color-border-secondary);
  overflow: hidden;

  .ant-image,
  .ant-image img {
    width: 100%;
    display: block;
  }
`;

const placeholder = css`
  background: var(--ant-color-bg-layout);
  color: var(--ant-color-text-tertiary);
  padding: var(--ant-padding-md);
  aspect-ratio: 210 / 297;
  cursor: pointer;
  transition: background var(--ant-motion-duration-mid);

  &:hover {
    background: var(--ant-color-fill-tertiary);
  }
`;

const fileIcon = css`
  font-size: 48px;
`;

const fileName = css`
  word-break: break-all;
`;

type FileData = Pick<CollectionFileFragment, "variants"> & Partial<Pick<CollectionFileFragment, "originalFilename">>;

type ImageFileProps = {
  readonly file: FileData;
  readonly alt?: string;
  readonly height?: number;
  readonly width?: number;
  readonly className?: string;
  readonly size?: "thumbnail" | "full";
};

const variant = (file: FileData, kind: VariantKind) =>
  file.variants.find((v) => v.kind === kind) ?? file.variants.find((v) => v.kind === VariantKind.Original);

const WEB_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif", "image/svg+xml", "image/avif"]);

const isWebImage = (file: FileData) => file.variants.some((v) => WEB_IMAGE_TYPES.has(v.contentType));

export const ImageFile = ({
  file,
  alt = undefined,
  height = undefined,
  width = undefined,
  className = undefined,
  size = "thumbnail",
}: ImageFileProps) => {
  if (!isWebImage(file)) {
    const original = variant(file, VariantKind.Original);
    return (
      <div className={wrapper}>
        <a download={file.originalFilename ?? true} href={original?.url} rel="noopener noreferrer" target="_blank">
          <Flex align="center" className={placeholder} gap="small" justify="center" vertical>
            <FileOutlined className={fileIcon} />
            {file.originalFilename
              ? (() => {
                  const dotIndex = file.originalFilename.lastIndexOf(".");
                  const hasExtension = dotIndex > 0;
                  const baseName = hasExtension ? file.originalFilename.slice(0, dotIndex) : file.originalFilename;
                  const extension = hasExtension ? file.originalFilename.slice(dotIndex) : "";
                  return (
                    <Typography.Text className={fileName} ellipsis={{ suffix: extension }} type="secondary">
                      {baseName}
                    </Typography.Text>
                  );
                })()
              : null}
            {original?.sizeBytes != null ? (
              <Typography.Text type="secondary">{formatFileSize(Number(original.sizeBytes))}</Typography.Text>
            ) : null}
          </Flex>
        </a>
      </div>
    );
  }

  const full = variant(file, VariantKind.FullSize);
  const thumb = variant(file, VariantKind.Thumbnail);
  const displayed = size === "full" ? full : thumb;
  const aspectRatio =
    displayed?.width != null && displayed?.height != null ? `${displayed.width} / ${displayed.height}` : undefined;

  return (
    <div className={wrapper}>
      <Image
        alt={alt}
        className={className}
        height={height}
        preview={{ src: full?.url }}
        src={displayed?.url}
        style={{ aspectRatio }}
        width={width}
      />
    </div>
  );
};
